/**
 * Deterministic row-height measurement — the height source F-10 windowing
 * needs for rows that have NO DOM node (docs/02 F-10 constraint (a), decided
 * in docs/10 §2c "HYBRID").
 *
 * Lifted from `packages/client/spike/measure.ts` after the 2026-07-16 scale
 * spike. It predicts the height a `.gt-cell` renders at WITHOUT mounting it,
 * by emulating the exact CSS the cell uses (`ui/app.css .gt-cell`):
 *
 *   box-sizing: border-box; padding: 3px 7px;
 *   border-right: 1px; border-bottom: 1px;
 *   font: 13px/1.4 <TABLE_FONT_FAMILY>;
 *   white-space: pre-wrap; overflow-wrap: anywhere;
 *
 * Line breaking emulated per CSS Text 3:
 *   - `pre-wrap` preserves "\n" (hard break) and preserves spaces, but a space
 *     at a soft-wrap opportunity HANGS at the end of the line (it does not
 *     count toward the line's width, and does not start the next line).
 *   - Soft-wrap opportunities on whitespace and CJK boundaries use the SAME
 *     classification core/table.ts ships for the F-26 run-width pass
 *     (`isBreakableWhitespace` / `isCjkBreakable`, imported, not restated).
 *     Sharing them is deliberate: if they drifted, F-26 column widening and
 *     row-height prediction would disagree about the same cell.
 *   - …but this module ALSO breaks after `-` and `/` (`BREAK_AFTER`), and
 *     core/table.ts does not. That is not drift, it is the two passes asking
 *     different questions, and it is worth being explicit about:
 *       · `splitUnwrappableRuns` asks "how wide must this column be so the run
 *         never has to hard-wrap?" and conservatively treats a URL as one
 *         atom — so a column holding a long URL widens to the 480 px cap.
 *       · `lineCount` asks "what will Chrome actually do at this width?" —
 *         and Chrome breaks URLs at hyphens and slashes (measured; docs/10
 *         §2a.4). Modelling the URL as unbreakable HERE would over-predict its
 *         height.
 *     The results stay consistent because the column has already widened by
 *     the time the height is predicted at that width.
 *   - `overflow-wrap: anywhere` breaks INSIDE a run only when the run does not
 *     fit on a line of its own (i.e. after a break was already taken).
 *
 * ACCURACY (measured, docs/10 §2a): 7 794/7 794 line boxes and 6 000/6 000
 * rows exact on the realistic corpus, 0 px cumulative drift. On adversarial
 * content 25/30 exact; the residue is ±1 line (±18.2 px) on Thai/Khmer (needs
 * ICU dictionaries), soft hyphens, and some space-free URLs. That residue is
 * why the strategy is HYBRID and not "predict only": the measured-on-mount
 * height is truth (see core/grid-sizing.ts `RowHeightCache`), the prediction
 * only fills rows that have never mounted, and it is applied as `min-height`
 * so F-26 "content never crops" holds and a wrong prediction self-heals.
 *
 * THREE RULES ARE LOAD-BEARING — each was forced by a measurement and none is
 * guessable. Dropping any one reintroduces drift:
 *   1. 1/64 px LayoutUnits (LINE_BOX_PX = 18.1875, not 18.2),
 *   2. RELAXED (`normal`, not `strict`) kinsoku clustering,
 *   3. UAX #14 break-after on `-` and `/`, suppressed before digits.
 *
 * Pure: every entry point takes the text-width function, so the whole module
 * is unit-testable without a DOM (vitest runs in node). The app passes
 * `boardTextWidth` (canvas `measureText`, memoized).
 */
import {
  isBreakableWhitespace,
  isCjkBreakable,
  TABLE_FONT,
  TABLE_FONT_PX,
  TABLE_LINE_HEIGHT,
} from "./table.js";

/**
 * `.gt-cell`'s REAL CSS box metrics. Deliberately not `CELL_H_PAD`/
 * `CELL_V_PAD` (8/4) from table.ts: those are the F-26 column-widening
 * budget, which pads its measurement with `RUN_WIDTH_SLOP` on purpose. The
 * height emulator has no slop to hide behind — it must use the literal
 * `padding: 3px 7px` + 1px borders the stylesheet sets, or every multi-line
 * row is wrong.
 */
export const CELL_PAD_TOP = 3;
export const CELL_PAD_BOTTOM = 3;
export const CELL_PAD_LEFT = 7;
export const CELL_PAD_RIGHT = 7;
export const CELL_BORDER_RIGHT = 1;
export const CELL_BORDER_BOTTOM = 1;

/** Content-box width of a cell in a track of `colWidth` board px. */
export function contentWidth(colWidth: number): number {
  return colWidth - CELL_PAD_LEFT - CELL_PAD_RIGHT - CELL_BORDER_RIGHT;
}

/**
 * Blink lays out in LayoutUnits of 1/64 px, not floats: a computed
 * `line-height: 1.4` on a 13px font is 18.2 px in CSS but 1164/64 = 18.1875 px
 * in layout. Ignoring that leaves a systematic +0.4 px per multi-line row —
 * harmless per row, but it accumulates over a 10k-row column (measured: 57.6 px
 * of drift over 1 000 rows before this was modelled, 0 px after).
 */
function toLayoutUnit(px: number): number {
  return Math.floor(px * 64) / 64;
}

/** The line box height a single line of `.gt-cell` text occupies. */
export const LINE_BOX_PX = toLayoutUnit(TABLE_FONT_PX * TABLE_LINE_HEIGHT); // 18.1875

/** Non-content vertical chrome of a `.gt-cell` (padding + bottom border). */
export const CELL_V_CHROME = CELL_PAD_TOP + CELL_PAD_BOTTOM + CELL_BORDER_BOTTOM; // 7

/**
 * Cache key component for every derived height (docs/10 §2c failure mode 3).
 *
 * THE ONE LATENT FOOT-GUN: there is no `@font-face` today — `.gt-cell`'s stack
 * resolves to system fonts, which canvas `measureText` and DOM layout share,
 * so a prediction made at any time is valid. **The moment a webfont is added**,
 * every height measured before it loads is measured against the fallback face.
 * Adding one therefore REQUIRES:
 *   1. bumping this version string (it keys `RowHeightCache`, so every cached
 *      prediction and measurement is discarded), and
 *   2. `await document.fonts.ready` before the first predict pass.
 * Neither is done here, because doing them speculatively would only pretend to
 * be a guard while the real one (the await) is missing.
 */
export const FONT_VERSION = `system:${TABLE_FONT}`;

/** Advance width of a string in the table font, in board px at zoom 1. */
export type MeasureText = (text: string) => number;

/* ------------------------------------------------------------------ */
/* Canvas text measurement (the app's MeasureText), memoized           */
/* ------------------------------------------------------------------ */

let ctx: CanvasRenderingContext2D | null = null;
const widthCache = new Map<string, number>();

/**
 * The app's `MeasureText`: canvas `measureText` in the table font, memoized per
 * exact string. Measured in BOARD units at zoom 1 only — docs/09 §1: re-wrapping
 * text at the zoomed font every frame was 4× over budget and gains nothing, so
 * zoom scales the derived heights, never the measurement.
 *
 * Shared by the height emulator here and by the F-26 run/line passes in
 * GridTable, so one warm cache serves both.
 */
export function boardTextWidth(text: string): number {
  const hit = widthCache.get(text);
  if (hit !== undefined) return hit;
  if (ctx === null) {
    const created = document.createElement("canvas").getContext("2d");
    if (created === null) return 0; // no 2d context (jsdom): predict 0-width text
    created.font = TABLE_FONT;
    ctx = created;
  }
  if (ctx.font !== TABLE_FONT) ctx.font = TABLE_FONT;
  const w = ctx.measureText(text).width;
  // Bounded: a 50k-row board atomizes into a few hundred thousand distinct
  // substrings. Clearing wholesale (rather than LRU-evicting) keeps this off
  // the hot path — the next pass re-measures, which is the 74 ms cold cost.
  if (widthCache.size > 200_000) widthCache.clear();
  widthCache.set(text, w);
  return w;
}

/** Drop the memoized widths (a font change is the only reason — FONT_VERSION). */
export function clearTextWidthCache(): void {
  widthCache.clear();
}

/* ------------------------------------------------------------------ */
/* Soft-wrap opportunity classification (SHARED with core/table.ts)    */
/* ------------------------------------------------------------------ */

// `isBreakableWhitespace` / `isCjkBreakable` are imported from core/table.ts
// rather than restated here. The F-26 run-width pass and this height emulator
// must agree, character for character, about where CSS may break a line — a
// second copy of the sets is a silent drift hazard, and drift here shows up as
// predicted edges that no longer match the rendered rows.

/**
 * KINSOKU (禁則処理) — the rule that made CJK the only inexact case.
 *
 * "Every CJK character is its own break opportunity" is wrong: Chrome's
 * default `line-break: auto` refuses to start a line with closing punctuation,
 * small kana, or sound marks, and refuses to end one with opening brackets.
 * Without this, `…読み、同じ…` predicted 3 lines where Chrome rendered 4
 * (measured at colWidth 200 — the 、 pushed down and took its neighbour with
 * it). It is modelled by CLUSTERING: a no-start character fuses to the atom
 * BEFORE it, a no-end character to the atom AFTER it. The clusters are then
 * ordinary unbreakable atoms and the greedy pass needs no special case.
 *
 * The set is the RELAXED one. `.gt-cell` does not set `line-break`, so it is
 * `auto`, which Chrome maps to `normal` — NOT `strict`. Under `normal`, small
 * kana (っゃゅょ…) and the prolonged sound mark ー ARE allowed to start a line;
 * only `strict` forbids them. Including them (the JIS X 4051 strict set) made
 * the emulator OVER-predict — `カメラは無人の街路をゆっくりと横切っていく。`
 * came out 3 lines where Chrome renders 2, at colWidth 160. Measured both ways
 * across a 100→420 px width sweep; relaxed is exact, strict is not.
 *
 * This is the common set, not the full UAX #14 table — see the accepted
 * failure modes in docs/10 §2c.
 */
const NO_START = new Set(
  // closing brackets / quotes
  "）〕］｝〉》」』】〙〗〟’”｠»)]}" +
    // punctuation that clings to the preceding character
    "、。，．：；？！・･｡､" +
    "…‥" +
    ",.:;?!%‰℃°",
);

const NO_END = new Set("（〔［｛〈《「『【〘〖〝‘“｟«([{");

/**
 * One atom of the line-breaking pass: a run of unbreakable characters, a
 * breakable-CJK cluster (one character, plus any kinsoku-fused neighbours), or
 * a single space (which can hang).
 */
interface Atom {
  readonly text: string;
  /** A space under pre-wrap: hangs at a line end instead of wrapping. */
  readonly isSpace: boolean;
}

/**
 * BREAK-AFTER characters (UAX #14 classes HY, SY, BA).
 *
 * MEASURED — this is the rule that invalidated the original "a URL is one
 * unbreakable run" premise. Chrome's break trace for
 * `17. https://example.com/…/anywhere-at-all-because-it-has-no-spaces` at
 * avail 465 is:
 *     line 0: "17. https://example.com/…/anywhere-at-all-"   (461.2 px)
 *     line 1: "because-it-has-no-spaces"
 * It broke at a HYPHEN, and at avail 345 it broke after a SLASH
 * ("…/never/breaks/" | "anywhere-at-all-because-nope"). URLs are NOT
 * unwrappable — they are full of break opportunities. `overflow-wrap:anywhere`
 * almost never fires on a real URL.
 *
 * Excluded in numeric context (UAX #14 LB25): `1-5` and `10/3` do not break,
 * which is why the next character being a digit suppresses the opportunity.
 *
 * U+00AD SOFT HYPHEN is also a break-after opportunity. Note the emulator does
 * NOT add the hyphen glyph that Chrome renders AT the break — an accepted
 * approximation (docs/10): soft hyphens do not occur in script content.
 */
const BREAK_AFTER = new Set("-/–—­");

function isDigit(ch: string | undefined): boolean {
  return ch !== undefined && ch >= "0" && ch <= "9";
}

function atomize(paragraph: string): Atom[] {
  const atoms: Atom[] = [];
  /** Set when the last emitted atom ends with a character that may not END a
   *  line — the next atom fuses onto it. */
  let pendingNoEnd = false;

  const emit = (text: string, isSpace: boolean) => {
    if (isSpace) {
      atoms.push({ text, isSpace: true });
      pendingNoEnd = false;
      return;
    }
    const prev = atoms[atoms.length - 1];
    const first = text[0] ?? "";
    const last = text[text.length - 1] ?? "";
    // Never fuse across a space: that IS a legal break opportunity.
    const fusable = prev !== undefined && !prev.isSpace;
    if (fusable && (pendingNoEnd || NO_START.has(first))) {
      atoms[atoms.length - 1] = { text: prev.text + text, isSpace: false };
      pendingNoEnd = NO_END.has(last);
      return;
    }
    atoms.push({ text, isSpace: false });
    pendingNoEnd = NO_END.has(last);
  };

  let run = "";
  const flushRun = () => {
    if (run.length > 0) {
      emit(run, false);
      run = "";
    }
  };
  const chars = Array.from(paragraph);
  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i] as string;
    if (isBreakableWhitespace(ch)) {
      flushRun();
      emit(ch, true);
      continue;
    }
    if (isCjkBreakable(ch.codePointAt(0) ?? 0)) {
      flushRun();
      emit(ch, false);
      continue;
    }
    run += ch;
    // A break opportunity AFTER this character ends the run here, so the next
    // atom can start a line (`anywhere-` | `at-all`, `breaks/` | `anywhere`).
    if (BREAK_AFTER.has(ch) && !isDigit(chars[i + 1])) flushRun();
  }
  flushRun();
  return atoms;
}

/**
 * Fill an over-long unbreakable run into the line, starting at `startW` of
 * already-used width, breaking it at arbitrary characters (`overflow-wrap:
 * anywhere`). Returns the number of EXTRA lines beyond the current one and the
 * width used on the last line.
 *
 * Modelled as: a run too wide for ANY line is NOT first moved to a fresh line —
 * Chrome keeps filling the current line and breaks the run where the line runs
 * out. This is the stated difference from `break-word` (which moves the word
 * down first), and app.css uses `anywhere`.
 *
 * HONESTY NOTE — this path is the least-verified rule in the file. It was
 * originally credited with fixing the URL line-box mismatches; the break trace
 * (docs/10 §2a item 4) later showed those URLs were breaking at HYPHENS and
 * SLASHES (now handled by BREAK_AFTER), and the fill rule had merely landed on
 * the same line count by coincidence. Real runs with NO break opportunity at
 * all are rare, so the rule rarely fires — and `long-url-nospace` /
 * `url-after-text` are still ±1 line at some widths (the residual adversarial
 * failures). If you need this exact, trace it with the spike's
 * `__spike.breakTrace` rather than trusting this comment.
 */
function fillRun(
  run: string,
  avail: number,
  startW: number,
  measure: MeasureText,
): { extraLines: number; lastWidth: number } {
  let extraLines = 0;
  let prefix = startW; // width already on this line, before the run's part
  let part = ""; // the run's characters on the CURRENT line
  for (const ch of run) {
    const next = part + ch;
    // Measure the CANDIDATE substring, never `width + charWidth`: shaping and
    // kerning make a run's advance narrower than the sum of its characters, so
    // summing breaks the line early.
    const w = prefix + measure(next);
    if (w > avail && (part !== "" || prefix > 0)) {
      extraLines += 1;
      prefix = 0;
      part = ch;
      continue;
    }
    part = next;
  }
  return { extraLines, lastWidth: prefix + measure(part) };
}

/**
 * Number of line boxes `text` occupies in a cell whose content box is
 * `avail` px wide. Empty text still occupies one line box (the cell's
 * strut) — matching how an empty `.gt-cell` renders.
 */
export function lineCount(text: string, avail: number, measure: MeasureText): number {
  if (avail <= 0) return 1;
  let lines = 0;
  for (const paragraph of text.split("\n")) {
    lines += 1; // every paragraph starts a line (pre-wrap keeps empty ones)
    if (paragraph === "") continue;
    let lineW = 0;
    let lineHasContent = false;
    for (const atom of atomize(paragraph)) {
      const w = measure(atom.text);
      if (atom.isSpace) {
        // pre-wrap PRESERVES spaces — including LEADING ones, which occupy
        // width at the start of a line (that is the whole point of pre-wrap;
        // `normal` would collapse them). A space only hangs when it lands at a
        // line END, where it never forces a wrap by itself.
        lineW += w;
        lineHasContent = true;
        continue;
      }
      if (!lineHasContent) {
        // First atom on the line (or the line only holds hanging spaces).
        if (w > avail) {
          const { extraLines, lastWidth } = fillRun(atom.text, avail, 0, measure);
          lines += extraLines;
          lineW = lastWidth;
        } else {
          lineW = w;
        }
        lineHasContent = true;
        continue;
      }
      if (lineW + w <= avail) {
        lineW += w;
        continue;
      }
      if (w > avail) {
        // Wider than any line: `anywhere` fills the REST of this line and
        // breaks inside the run — it does not move the run down first.
        const { extraLines, lastWidth } = fillRun(atom.text, avail, lineW, measure);
        lines += extraLines;
        lineW = lastWidth;
        continue;
      }
      // Fits on a line of its own: take the soft-wrap opportunity before it.
      lines += 1;
      lineW = w;
    }
  }
  return Math.max(1, lines);
}

/**
 * Predicted height of one `.gt-cell` in LAYOUT space (still fractional — the
 * grid row track sizes to the max of these, and only THAT is rounded).
 */
export function cellHeight(
  text: string,
  colWidth: number,
  minHeight: number,
  measure: MeasureText,
): number {
  const lines = lineCount(text, contentWidth(colWidth), measure);
  return Math.max(minHeight, lines * LINE_BOX_PX + CELL_V_CHROME);
}

/**
 * Predicted rendered height (whole board px) of a row — the tallest cell
 * (F-26 "row = tallest cell"), floored by the stored min height (F-14 manual
 * resize AND the F-36 element ratchet — docs/04 §5), ALWAYS ROUNDED UP.
 *
 * Rounding at the ROW and not per cell is what makes the prediction exact: the
 * grid row track is sized once, from the max of the fractional cell heights.
 *
 * WHY `ceil` AND NOT `round` — measured, and the reason is not obvious.
 * Predicted edges are integers, but a grid row track sizes itself to the
 * FRACTIONAL intrinsic height (43.375, not 43). Applying the prediction as a
 * `min-height` therefore LOSES — the track keeps its larger intrinsic size —
 * and the mounted block creeps away from the predicted edges by ~0.05 px/row
 * (measured: mean 1.2 px, max 4.4 px over a 66-row block). Selection rects and
 * cell-anchored pins resolve against the predicted edges, so that creep is
 * visible misalignment.
 *
 * `ceil` removes it BY CONSTRUCTION: ceil(intrinsic) > intrinsic always, so the
 * `min-height` always wins and the used height IS the predicted integer. Rows
 * come out up to 0.6 px taller than the browser would make them alone — which
 * is not an error, because with windowing the prediction IS the geometry: no
 * unmounted row has a browser opinion to disagree with. It also fails SAFE for
 * F-26 "content never crops": ceil can only over-shoot.
 *
 * Verified 0 px alignment error at 200/1k/10k/50k rows, top and deep-panned.
 */
export function predictRowHeight(
  values: readonly string[],
  colWidths: readonly number[],
  minHeight: number,
  measure: MeasureText,
): number {
  let h = minHeight;
  for (let c = 0; c < values.length; c++) {
    const w = colWidths[c];
    const v = values[c];
    if (w === undefined || v === undefined) continue;
    const ch = cellHeight(v, w, minHeight, measure);
    if (ch > h) h = ch;
  }
  return Math.ceil(h);
}
